import * as React from 'react';
import { useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select from '@mui/material/Select';
import Grid from '@mui/material/Unstable_Grid2';
import Organisation from 'benchmarking-domain/organisation';
import { PermissionsHelper } from 'benchmarking-domain/permissions-helper';
import Plant, { PlantRegion } from 'benchmarking-domain/plant';
import _default from 'currency-codes-ts';

import { useOrganisation } from '../../../hooks/use-organisation';
import { useUser } from '../../../hooks/use-user';
import { UserSelect } from '../../core/shared/user-select';

interface PlantFormProps {
  plant: Plant;
  onSubmit: (data: Plant) => Promise<void>;
}

export function PlantForm({ plant, onSubmit }: PlantFormProps): React.JSX.Element {
  const { organisationId } = useOrganisation();
  const { user } = useUser();

  const [prevPlant, setPrevPlant] = useState<Plant | undefined>(plant);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const [managers, setManagers] = useState<string[]>(plant?.managers ?? []);

  if (prevPlant !== plant) {
    setPrevPlant(plant);
    setManagers(plant?.managers ?? []);
  }

  const saveData = async (): Promise<void> => {
    setIsSaving(true);

    try {
      await onSubmit({ ...plant, id: plant?.id ?? '', managers });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form
      onSubmit={async (event) => {
        event.preventDefault();
        await saveData();
      }}
    >
      <Card>
        <CardHeader subheader="The information can be edited" title={plant ? 'Plant' : 'New plant'} />
        <Divider />
        <CardContent>
          <Grid container spacing={3}>
            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Name</InputLabel>
                <OutlinedInput defaultValue={plant.name} label="Name" name="name" disabled />
              </FormControl>
            </Grid>
            <Grid md={6} xs={12}>
              <FormControl fullWidth>
                <InputLabel>Region</InputLabel>
                <Select value={plant.region} label="Region" name="region" variant="outlined" disabled>
                  {Object.values(PlantRegion).map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Country</InputLabel>
                <Select
                  label="Country"
                  id="country-select"
                  variant="outlined"
                  value={plant.country}
                  disabled
                  sx={{ color: 'inherit' }}
                >
                  {_default
                    .countries()
                    .sort((one, two) => (one < two ? -1 : 1))
                    .map((countryRecord) => (
                      <MenuItem key={countryRecord} value={countryRecord}>
                        {countryRecord}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <Autocomplete
                  multiple
                  id="technologies-filled"
                  options={[]}
                  freeSolo
                  autoSelect
                  value={plant.technologies}
                  disabled
                  renderTags={(value: string[], getTagProps) =>
                    value.map((option: string, index: number) => (
                      <Chip variant="outlined" label={option} {...getTagProps({ index })} key={option} />
                    ))
                  }
                  renderInput={(params) => <TextField {...params} variant="outlined" label="Technologies" />}
                />
              </FormControl>
            </Grid>
            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <UserSelect
                  id="managers-filled"
                  organisationId={organisationId}
                  label="Managers"
                  value={managers}
                  onChange={setManagers}
                  disabled={!PermissionsHelper.hasUserAtLeastRole('organisationManager', user, plant)}
                />
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
        <Divider />
        <CardActions sx={{ justifyContent: 'flex-end' }}>
          <Button
            type="submit"
            variant="contained"
            disabled={isSaving || !PermissionsHelper.hasUserAtLeastRole('organisationManager', user, plant)}
          >
            {plant ? 'Save details' : 'Create new plant'}
          </Button>
        </CardActions>
      </Card>
    </form>
  );
}
